import React, { useRef, useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useAllEvents } from './useAllEvents';
import { Alert } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { themeAlpine } from 'ag-grid-community';
import { Event } from '../services/Types';
import { useEvent } from './useEvent';
import { useMyEvents } from './useMyEvents';

interface UseEventGridProps {
  columnDefs: ColDef[];
  gridStyle?: React.CSSProperties;
  allEvents?: boolean;
}

interface UseEventGridReturn {
  gridRef: React.RefObject<AgGridReact>;
  events: any[];
  defaultColDef: any;
  selectedEvent: Event | null;
  renderGrid: (props?: renderGridProps) => JSX.Element;
}

interface renderGridProps {
  rowHeight?: number;
}

export const useEventGrid = ({ columnDefs, gridStyle, allEvents = true }: UseEventGridProps): UseEventGridReturn => {
  const gridRef = useRef<AgGridReact>(null);
  const { events: allEventsList, errorUseAllEvents: allEventsError } = useAllEvents();
  const { myEvents: myEventsList, errorUseMyEvents: myEventsError } = useMyEvents("future");
  const events = allEvents ? allEventsList : myEventsList;
  const errorUseAllEvents = allEvents ? allEventsError : myEventsError;
  const { eventData, getEventData } = useEvent();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    resizable: false,
  }), []);

  const defaultGridStyle = useMemo(() => ({ 
    height: "calc(100vh - 473px)", 
    width: "100%" 
  }), []);

  useEffect(() => {
    setSelectedEvent(eventData as Event);
  }, [eventData]);

  // TODO: need to accept children to render the grid so that EventGrid can use this hook for its special AgGridReact call
  const renderGrid = (props?: renderGridProps): JSX.Element => {
    return (
      <div style={gridStyle || defaultGridStyle}>
        {errorUseAllEvents && <Alert variant="filled" severity="error">{errorUseAllEvents}</Alert>}
        <AgGridReact
          ref={gridRef}
          theme={themeAlpine}
          rowData={events}
          rowHeight={props?.rowHeight || 70}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellClicked={(event) => {
            // fetch a full Event object from the database
            getEventData(event.data.id)
          }}
        />
      </div>
    );
  };

  return {
    gridRef,
    events,
    defaultColDef,
    selectedEvent,
    renderGrid
  };
}; 